import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, NumberArray, Texture, Object3D } from '@wonderlandengine/api';
import { Configuration, GearStickOptions, LeatherTypes, SeatBackStyles, SeatPleatingStyles, TunnelSidesOptions, EmbroideryOptions, SteeringWheelOptions, SteeringColumnOptions } from '../data/configuration.js';
import { LeatherDescription } from './components/leather-description.js';
import { CarpetDescription } from './components/carpet-description.js';
import { DashFaceDescription } from './components/dash-face-description.js';
import { ColourDescription } from './components/colour-description.js';
import { PaintDescription } from './components/paint-description.js';
import { CentreConsoleDescription } from './components/centre-console-description.js';
import { Utilities } from '../utilities/utilities.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';
import { CarPbrMaterial, cloneBodyPaintMaterial } from './paint-configurator.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SeatMaterial extends Material {
	seatColor: NumberArray;
	pipeColor: NumberArray;
	stitchingColor: NumberArray;
	albedoTexture: Texture;
	metallicTexture: Texture;
	normalTexture: Texture;
	detailNormalTexture: Texture;
	stitchesMaskTexture: Texture;
	metallicIntensity: number;
	smoothnessIntensity: number;
	detailNormalIntensity: number;
}

interface SeatTextileMaterial extends Material {
	albedoColor: NumberArray;
}

export interface CarpetMaterial extends Material {
	albedoColor: NumberArray;
}

type FadableMaterial = Material | CarpetMaterial | SeatMaterial;
type FadableKey = {
	[TK in keyof FadableMaterial]: FadableMaterial[TK] extends NumberArray ? TK : never;
}[keyof FadableMaterial];

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;
const GLOSS_SMOOTHNESS_VALUE = 0.9;
const GLOSS_REFLECTIVE_SPECULAR_VALUE = 0.96;
const MATT_SMOOTHNESS_VALUE = 0.4;
const MATT_REFLECTIVE_SPECULAR_VALUE = 0.4;

/**
 * Interior Configurator:
 */
export class InteriorConfigurator extends Component {
	static TypeName = 'interior-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	leatherDescriptions!: Object3D;

	@property.object({ required: true })
	seatStitchingDescriptions!: Object3D;

	@property.object({ required: true })
	paintDescriptions!: Object3D;

	@property.object({ required: true })
	carpetDescriptions!: Object3D;

	@property.object({ required: true })
	dashFaceDescriptions!: Object3D;

	@property.object({ required: true })
	centreConsoleDescriptions!: Object3D;

	@property.object({ required: true })
	veneerLogoS400!: Object3D;

	@property.object({ required: true })
	tunnelCarpet!: Object3D;

	@property.object({ required: true })
	tunnelLeather!: Object3D;

	@property.object({ required: true })
	tunnelLeather400!: Object3D;

	@property.object({ required: true })
	tunnelPocket!: Object3D;

	@property.object({ required: true })
	interiorSideScreen!: Object3D;

	@property.object({ required: true })
	doorLeather!: Object3D;

	@property.object({ required: true })
	doorSills!: Object3D;

	@property.object({ required: true })
	dashboardLeather!: Object3D;

	@property.object({ required: true })
	dashboardLeather400!: Object3D;

	@property.object({ required: true })
	dashboardAlcantara400!: Object3D;

	@property.object({ required: true })
	doorLeather400!: Object3D;

	@property.object({ required: true })
	doorTops400!: Object3D;

	@property.object({ required: true })
	hardTopLeather!: Object3D;

	@property.object({ required: true })
	interiorRear!: Object3D;

	@property.object({ required: true })
	seatBase!: Object3D;

	@property.object({ required: true })
	seatBoxStitch!: Object3D;

	@property.object({ required: true })
	seatDiamondStitch!: Object3D;

	@property.object({ required: true })
	seatHorizontalStitch!: Object3D;

	@property.object({ required: true })
	seatVerticalStitch400!: Object3D;

	@property.object({ required: true })
	seatBackLeather!: Object3D;

	@property.object({ required: true })
	seatBackTextile!: Object3D;
	
	@property.object({ required: true })
	visors!: Object3D;

	@property.object({ required: true })
	gearstickLeather!: Object3D;

	@property.object({ required: true })
	gearstickLeather400!: Object3D;

	@property.object({ required: true })
	handbrakeLeather!: Object3D;

	@property.object({ required: true })
	carpets!: Object3D;

	@property.object({ required: true })
	carpetPiping!: Object3D;

	@property.object({ required: true })
	seatHeadrestStitch!: Object3D;

	@property.object({ required: true })
	seatHeadrestCustomStitch!: Object3D;

	@property.object({ required: true })
	dashFace!: Object3D;

	@property.object({ required: true })
	steeringWheelSilver!: Object3D;

	@property.object({ required: true })
	steeringWheelBlack!: Object3D;

	@property.object({ required: true })
	steeringWheelCrown!: Object3D;

	@property.object({ required: true })
	steeringColumnComposite!: Object3D;

	@property.object({ required: true })
	steeringColumnLeather!: Object3D;

	@property.object({ required: true })
	centreConsoleWood!: Object3D;

	@property.object({ required: true })
	centreConsoleMarquetry!: Object3D;

	@property.object({ required: true })
	dashLowerRail!: Object3D;

	@property.object({ required: true })
	doorWoodPanel!: Object3D;

	@property.object({ required: true })
	standardGearStick!: Object3D;

	@property.object({ required: true })
	aluminiumGearStick400!: Object3D;

	@property.material({ required: true })
	seatsS400LeatherMaterial!: SeatMaterial;

	@property.material({ required: true })
	seatsS400AlcantaraMaterial!: SeatMaterial;

	@property.material({ required: true })
	seatsAlcantaraMaterial!: SeatMaterial;

	@property.material({ required: true })
	blackAlcantaraS400Material!: SeatTextileMaterial;

	@property.material({ required: true })
	steeringWheelLeatherMaterial!: Material;

	@property.material({ required: true })
	steeringWheelAlcantaraMaterial!: Material;

	@property.material({ required: true })
	doorLeatherStandardMaterial!: SeatMaterial;

	@property.material({ required: true })
	doorLeatherExtendedStitchS400Material!: SeatMaterial;
	
	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _leatherDescriptions!: LeatherDescription[];
	private _seatStitchingDescriptions!: ColourDescription[];
	private _paintDescriptions!: PaintDescription[];
	private _carpetDescriptions!: CarpetDescription[];
	private _dashFace!: MeshComponent;
	private _centreConsoleDescriptions!: CentreConsoleDescription[];
	private _tunnelLeather!: MeshComponent;
	private _tunnelLeather400!: MeshComponent;
	private _tunnelCarpet!: MeshComponent;
	private _tunnelPocket!: MeshComponent;
	private _interiorSideScreen!: MeshComponent;
	private _doorLeather!: MeshComponent;
	private _dashboardLeather!: MeshComponent;
	private _dashboardLeather400!: MeshComponent;
	private _dashboardAlcantara400!: MeshComponent;
	private _doorLeather400!: MeshComponent;
	private _doorTops400!: MeshComponent;
	private _seatBase!: MeshComponent;
	private _seatBoxStitch!: MeshComponent;
	private _seatDiamondStitch!: MeshComponent;
	private _seatHorizontalStitch!: MeshComponent;
	private _seatVerticalStitch400!: MeshComponent;
	private _seatBackLeather!: MeshComponent;
	private _seatBackTextile!: MeshComponent;
	private _visors!: MeshComponent;
	private _gearstickLeather!: MeshComponent;
	private _gearstickLeather400!: MeshComponent;
	private _handbrakeLeather!: MeshComponent;
	private _hardTopLeather!: MeshComponent;
	private _doorSills!: MeshComponent;
	private _interiorRear!: MeshComponent;
	private _carpets!: MeshComponent;
	private _carpetPiping!: MeshComponent;
	private _seatHeadrestStitch!: MeshComponent;
	private _seatHeadrestCustomStitch!: MeshComponent;
	private _dashFaceDescriptions!: DashFaceDescription[];
	private _centreConsoleWood!: MeshComponent;
	private _centreConsoleMarquetry!: MeshComponent;
	private _dashLowerRail!: MeshComponent;
	private _doorWoodPanel!: MeshComponent;
	private _steeringWheelCrown!: MeshComponent;

	private _lastLeatherType: LeatherTypes | null = null;
	private _lastContrastOptionId: string | null = null;

	// the dash face material while a body-matching dashboard option is selected, kept so that a changing body colour can be written
	// straight onto it rather than rebuilding it. null whenever the selected dashboard option does not follow the body colour.
	private _bodyMatchingDashboardMaterial: CarPbrMaterial | null = null;

	private _tweenManager = new TweenManager();

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start(): void {

		// get components.
		this._leatherDescriptions = this.leatherDescriptions.getComponents(LeatherDescription)!;
		this._seatStitchingDescriptions = this.seatStitchingDescriptions.getComponents(ColourDescription)!;
		this._paintDescriptions = this.paintDescriptions.getComponents(PaintDescription);
		this._carpetDescriptions = this.carpetDescriptions.getComponents(CarpetDescription);
		this._dashFaceDescriptions = this.dashFaceDescriptions.getComponents(DashFaceDescription);
		this._centreConsoleDescriptions = this.centreConsoleDescriptions.getComponents(CentreConsoleDescription);
		this._seatBase = this.seatBase.getComponent(MeshComponent)!;
		this._doorLeather = this.doorLeather.getComponent(MeshComponent)!;
		this._dashboardLeather = this.dashboardLeather.getComponent(MeshComponent)!;
		this._dashboardLeather400 = this.dashboardLeather400.getComponent(MeshComponent)!;
		this._dashboardAlcantara400 = this.dashboardAlcantara400.getComponent(MeshComponent)!;
		this._doorLeather400 = this.doorLeather400.getComponent(MeshComponent)!;
		this._doorTops400 = this.doorTops400.getComponent(MeshComponent)!;
		this._tunnelCarpet = this.tunnelCarpet.getComponent(MeshComponent)!;
		this._tunnelLeather = this.tunnelLeather.getComponent(MeshComponent)!;
		this._tunnelLeather400 = this.tunnelLeather400.getComponent(MeshComponent)!;
		this._tunnelPocket = this.tunnelPocket.getComponent(MeshComponent)!;
		this._visors = this.visors.getComponent(MeshComponent)!;
		this._gearstickLeather = this.gearstickLeather.getComponent(MeshComponent)!;
		this._gearstickLeather400 = this.gearstickLeather400.getComponent(MeshComponent)!;
		this._handbrakeLeather = this.handbrakeLeather.getComponent(MeshComponent)!;
		this._interiorSideScreen = this.interiorSideScreen.getComponent(MeshComponent)!;
		this._interiorRear = this.interiorRear.getComponent(MeshComponent)!;
		this._hardTopLeather = this.hardTopLeather.getComponent(MeshComponent)!;
		this._doorSills = this.doorSills.getComponent(MeshComponent)!;
		this._seatBackLeather = this.seatBackLeather.getComponent(MeshComponent)!;
		this._seatBackTextile = this.seatBackTextile.getComponent(MeshComponent)!;
		this._seatBoxStitch = this.seatBoxStitch.getComponent(MeshComponent)!;
		this._seatDiamondStitch = this.seatDiamondStitch.getComponent(MeshComponent)!;
		this._seatHorizontalStitch = this.seatHorizontalStitch.getComponent(MeshComponent)!;
		this._seatVerticalStitch400 = this.seatVerticalStitch400.getComponent(MeshComponent)!;
		this._carpets = this.carpets.getComponent(MeshComponent)!;
		this._carpetPiping = this.carpetPiping.getComponent(MeshComponent)!;
		this._dashFace = this.dashFace.getComponent(MeshComponent)!;
		this._dashLowerRail = this.dashLowerRail.getComponent(MeshComponent)!;
		this._centreConsoleWood = this.centreConsoleWood.getComponent(MeshComponent)!;
		this._centreConsoleMarquetry = this.centreConsoleMarquetry.getComponent(MeshComponent)!;
		this._doorWoodPanel = this.doorWoodPanel.getComponent(MeshComponent)!;
		this._seatHeadrestStitch = this.seatHeadrestStitch.getComponent(MeshComponent)!;
		this._seatHeadrestCustomStitch = this.seatHeadrestCustomStitch.getComponent(MeshComponent)!;
		this._steeringWheelCrown = this.steeringWheelCrown.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Selects gear stick style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectGearStickOption(id: string) {

		// if aluminium gear stick is selected, enable the aluminium gear stick and disable the standard gear stick components.
		// n.b. the aluminium gear stick is available as an option on the Supersport 400 model, and is the standard option on the Supersport model in the US region.
		if (id === GearStickOptions.Aluminium || Configuration.isSupersportModelInUsRegion()) {
			Utilities.setActiveAll(this.aluminiumGearStick400, true);
			this.standardGearStick.active = false;
			this.gearstickLeather.active = false;
		}
		else {
			Utilities.setActiveAll(this.aluminiumGearStick400, false);
			this.standardGearStick.active = true;
			this.gearstickLeather.active = true;
		}
	}

	/**
	* Selects centre console style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectCentreConsoleOption(id: string) {

		// get the centre console description for the given ID.
		const centreConsoleDescription = this._centreConsoleDescriptions.find(element => element.consoleId === id);
		if (!centreConsoleDescription) {
			console.error('No centre console description found for id: ', id);
			return;
		}

		// get the material from the description.
		const consoleMaterial = centreConsoleDescription.consoleMaterial?.clone() as Material;
		if (!consoleMaterial) {
			console.error('No centre console material assigned for id: ', id);
			return;
		}

		// get the object from the description.
		const consoleObject = centreConsoleDescription.consoleObject as Object3D;
		if (!consoleObject) {
			console.error('No console object assigned for id: ', id);
			return;
		}

		// get the mesh component of the object.
		const consoleComponent = consoleObject.getComponent(MeshComponent)!;
		if (!consoleComponent) {
			console.error('No console component assigned for id: ', id);
			return;
		}

		// set all consoles to false initially.
		this._centreConsoleWood.active = false;
		this._centreConsoleMarquetry.active = false;

		// set the relevant console to active with its material.
		consoleComponent.active = true;

		// update the console material.
		consoleComponent.material = consoleMaterial;

		// get the rail material from the description.
		const railMaterial = centreConsoleDescription.railMaterial?.clone() as Material;
		if (!railMaterial) {
			console.error('No rail material assigned for id: ', id);
			return;
		}

		// update the lower rail and door wood materials.
		this._dashLowerRail.material = railMaterial;
		this._doorWoodPanel.material = railMaterial;

		// for the Supersport 400, we also need to show the veneer logo on the centre console if the veneer option is selected.
		this.veneerLogoS400.active = (id === Configuration.centreConsoleOptionForS400Veneer);
	}

	/**
	* Selects steering wheel style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSteeringWheelOption(id: string) {

		// if supersport 400 model...
		if (Configuration.isSupersport400Model()) {

			// enable the standard silver steering wheel and disable the black steering wheel, as it's not valid for this model.
			this.steeringWheelSilver.active = true;
			this.steeringWheelBlack.active = false;

			// set the steering wheel crown to either the Alcántara or leather material depending on the selected option.
			if (id === SteeringWheelOptions.BlackAlcantara) {
				this._steeringWheelCrown.material = this.steeringWheelAlcantaraMaterial;
			}
			else {
				this._steeringWheelCrown.material = this.steeringWheelLeatherMaterial;
			}
		}

		// or, for the standard model, select either the silver or black steering wheel.
		else {
			const silverSteeringWheelSelected = (id === SteeringWheelOptions.Silver);
			this.steeringWheelSilver.active = silverSteeringWheelSelected;
			this.steeringWheelBlack.active = !silverSteeringWheelSelected;
		}
	}

	/**
	* Selects steering column style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSteeringColumnOption(id: string) {
		const compositeColumnSelected = id === SteeringColumnOptions.BlackComposite;

		this.steeringColumnComposite.active = compositeColumnSelected;
		this.steeringColumnLeather.active = !compositeColumnSelected;
	}

	/**
	* Selects a dashboard face style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectDashboardOption(id: string) {
		const dashboardFace = this._dashFace;
		let material: CarPbrMaterial;

		// check if the dashboad item is the same as body colour.
		if (id === Configuration.dashboardMatchingGlossBodyColourId ||
			id === Configuration.dashboardMatchingMatteBodyColourId) {

			// get a material for the current body paint colour, which handles a bespoke colour having no paint description of its own.
			const bodyPaintMaterial = cloneBodyPaintMaterial(this._paintDescriptions);
			if (!bodyPaintMaterial) {
				return;
			}
			material = bodyPaintMaterial;

			// update the gloss and smoothness depending on whether matte or gloss is selected.
			if (Configuration.dashboardOption === Configuration.dashboardMatchingGlossBodyColourId) {
				material.smoothnessFactor = GLOSS_SMOOTHNESS_VALUE;
				material.clearCoatSmoothness = GLOSS_REFLECTIVE_SPECULAR_VALUE;
			} else {
				material.smoothnessFactor = MATT_SMOOTHNESS_VALUE;
				material.clearCoatSmoothness = MATT_REFLECTIVE_SPECULAR_VALUE;
			}

			// update the material.
			dashboardFace.material = material;

			// keep it, so that a body colour changing afterwards can be written straight onto it - see updateDashboardBodyColour().
			this._bodyMatchingDashboardMaterial = material;

			return;
		}

		// any other dashboard option does not follow the body colour, so nothing is left for updateDashboardBodyColour() to write to.
		this._bodyMatchingDashboardMaterial = null;

		const dashFaceDescription = this._dashFaceDescriptions.find(element => element.dashboardId === id);

		if (!dashFaceDescription) {
			console.error('No dashboard description found for id: ', id);
			return;
		}

		// get the relevant material.
		material = dashFaceDescription.dashboardMaterial?.clone() as CarPbrMaterial;

		if (!material) {
			console.error('No paint material assigned for id: ', id);
			return;
		}

		// apply the material to the dashboard face, unable to fade colour because of switching between gloss and matte.
		dashboardFace.material = material;
	}

	/**
	* Writes the given body colour, as an rgba array of 0–1 floats, onto a body-matching dashboard face, so that it keeps up with a
	* bespoke colour as it is chosen. Does nothing unless a body-matching dashboard option is selected.
	* This overwrites the albedo of the material selectDashboardOption() already built, rather than rebuilding it, so that it is cheap
	* enough to call on every movement of the colour picker - the same trade as PaintConfigurator.selectBespokeBodyPaint() makes.
	* The finish is therefore left exactly as that option set it: only the colour changes.
	*/
	updateDashboardBodyColour(colour: NumberArray) {

		// nothing to do unless the dash face is currently wearing a body-matching material.
		if (!this._bodyMatchingDashboardMaterial) {
			return;
		}

		// overwrite its albedo with the given colour.
		this._bodyMatchingDashboardMaterial.albedoColor = [colour[0], colour[1], colour[2], colour[3] ?? 1];
	}

	/**
	* Selects a tunnel side style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectCarpetColour(id: string) {
		const carpetDescription = this._carpetDescriptions.find(element => element.carpetId === id);

		if (!carpetDescription) {
			console.error('No carpet description found for id: ', id);
			return;
		}

		// get the relevant material.
		const material = carpetDescription.carpetMaterial as CarpetMaterial;

		// get the relevant mesh components.
		const carpets = this._carpets;
		const tunnelCarpet = this._tunnelCarpet;

		// apply the relevant materials.
		if (material) {
			this._genericFadeColour(carpets, material.albedoColor, 'albedoColor' as FadableKey);
			this._genericFadeColour(tunnelCarpet, material.albedoColor, 'albedoColor' as FadableKey);
		}
	}

	/**
	* Selects a tunnel side style with the Configuration selection, updating the relevant meshes/materials on the vehicle model.
	*/
	selectTunnelSidesStyle() {

		// if leather is selected, disable the tunnel carpet mesh, and enable the relevant leather tunnel sides mesh.
		if (Configuration.tunnelSidesStyle === TunnelSidesOptions.Leather) {

			this.tunnelCarpet.active = false;
			if (Configuration.seatPleatingOption === SeatPleatingStyles.ExtendedS400) {
				this.tunnelLeather400.active = true;
				this.tunnelLeather.active = false;
			}
			else {
				this.tunnelLeather400.active = false;
				this.tunnelLeather.active = true;
			}
		}

		// if carpet is selected, enable the tunnel carpet and disable both leather tunnel sides meshes.
		else {
			this.tunnelCarpet.active = true;
			this.tunnelLeather.active = false;
			this.tunnelLeather400.active = false;
		}
	}

	/**
	* Selects a seat pleating style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSeatPleatingStyle(id: string) {

		// activate the appropriate seat pleating object, and deactivate the rest.
		this.seatHorizontalStitch.active = false;
		this.seatDiamondStitch.active = false;
		this.seatBoxStitch.active = false;
		this.seatVerticalStitch400.active = false;
		switch (id as SeatPleatingStyles) {
			case SeatPleatingStyles.Horizontal:
				this.seatHorizontalStitch.active = true;
				break;
			case SeatPleatingStyles.Quilted:
				this.seatDiamondStitch.active = true;
				break;
			case SeatPleatingStyles.Box:
				this.seatBoxStitch.active = true;
				break;

			// vertical and extended pleating styles on the Supersport 400 both show the vertical stitch on the seats.
			case SeatPleatingStyles.VerticalS400:
			case SeatPleatingStyles.ExtendedS400:
				this.seatVerticalStitch400.active = true;
				break;
			default:
				console.error('Invalid id passed to "selectSeatPleatingStyle" function ', id);
				break;
		}

		// for the extended pleating style on the Supersport 400, we also need to show the extended stitching on the doors and transmission tunnel,
		// so re-select the tunnel sides style to enable/disable the relevant meshes.
		this.selectTunnelSidesStyle();

		// for the Supersport 400, we also need to set the door leather material to either the standard material or the extended stitching material,
		// but retaining the current colour selection (leather colour and optionally stitching colour).
		if (Configuration.isSupersport400Model()){

			// persist the colours from the current seat base material.
			const currentMaterial = this._seatBase.material as SeatMaterial;
			const seatColour = currentMaterial.seatColor;
			const pipeColour = currentMaterial.pipeColor;
			const stitchingColour = currentMaterial.stitchingColor;

			// if extended stitching is selected, set the door leather to the extended stitch material, which has a stitch mask, and retain the current colour selection.
			if (id === SeatPleatingStyles.ExtendedS400) {
				const newMaterial = this.doorLeatherExtendedStitchS400Material.clone() as SeatMaterial;
				newMaterial.seatColor = seatColour;
				newMaterial.pipeColor = pipeColour;
				newMaterial.stitchingColor = stitchingColour;
				this._doorLeather400.material = newMaterial;
			}

			// otherwise, set the door leather to the standard material, which doesn't have a stitch mask, and set the stitch colour to match the seat leather colour.
			else {
				const newMaterial = this.doorLeatherStandardMaterial.clone() as SeatMaterial;
				newMaterial.seatColor = seatColour;
				newMaterial.pipeColor = seatColour;
				newMaterial.stitchingColor = seatColour;
				this._doorLeather400.material = newMaterial;
			}
		}
	}
	
	/**
	* Selects an embroidery style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectEmbroideryOption(id: string) {

		// toggle the wings embroidery.
		if (id === EmbroideryOptions.Wings) {
			this.seatHeadrestStitch.active = true;
			this.seatHeadrestCustomStitch.active = false;
		}
		// toggle custom embroidery.
		else if (id === EmbroideryOptions.Custom) {
			this.seatHeadrestStitch.active = false;
			this.seatHeadrestCustomStitch.active = true;
		}
		// remove all embroidery.
		else {
			this.seatHeadrestStitch.active = false;
			this.seatHeadrestCustomStitch.active = false;
		}
	}
	
	/**
	* Selects a seat back style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSeatBackStyle(id: string) {

		// activate the appropriate seat back object, and deactivate the rest.
		this.seatBackLeather.active = false;
		this.seatBackTextile.active = false;
		switch (id as SeatBackStyles) {
			case SeatBackStyles.LeatherWrapped:
				this.seatBackLeather.active = true;
				break;
			case SeatBackStyles.TwoToneTextile:
				this.seatBackTextile.active = true;
				break;
			default:
				console.error('Invalid id passed to "selectSeatBackStyle" function ', id);
				break;
		}
	}

	/**
	* Selects a seat stitching style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSeatStitchingStyle(id: string) {

		// use leather material colour if matched leather selected.
		let colour: NumberArray;
		if (id === Configuration.stitchingMatchingLeatherStyleId) {
			const leatherDescription = this._leatherDescriptions.find(element => element.leatherId === Configuration.leatherStyle);
			if (!leatherDescription) {
				console.error('No leather description found for id: ', id);
				return;
			}
			colour = (leatherDescription!.leatherMaterial as SeatMaterial).stitchingColor;
		}
		// otherwise get the stitching description for the given ID.
		else {
			const seatStitchingDescription = this._seatStitchingDescriptions.find(element => element.colourId === id);
			if (!seatStitchingDescription) {
				console.error('No seat stitching description found for id: ', id);
				return;
			}
			colour = seatStitchingDescription.colour;
		}

		// smoothly fade between the current stitch colour and the selected colour.
		this._genericFadeColour(this._seatBase, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatBoxStitch, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatDiamondStitch, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatHorizontalStitch, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatVerticalStitch400, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._tunnelLeather400, colour, 'stitchingColor' as FadableKey);
		this._genericFadeColour(this._seatHeadrestStitch, colour, 'ambientColor' as FadableKey);
		this._genericFadeColour(this._seatHeadrestCustomStitch, colour, 'ambientColor' as FadableKey);

		// we only want to fade the door leather stitch colour for the Supersport 400 with the extended stitching option, as otherwise it would change the leather colour.
		if (Configuration.seatPleatingOption === SeatPleatingStyles.ExtendedS400) {
			this._genericFadeColour(this._doorLeather400, colour, 'stitchingColor' as FadableKey);
		}
	}

	/**
	* Selects a leather style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectLeatherStyle(id: string, contrastOptionId: string) {

		// get the leather description for the given ID.
		const leatherDescription = this._leatherDescriptions.find(element => element.leatherId === id);
		if (!leatherDescription) {
			console.error('No leather description found for id: ', id);
			return;
		}

		// get the leather type.
		const leatherType = leatherDescription.leatherType;
		const isTwoTone = leatherType === LeatherTypes.TwoTone;

		// get the relevant materials - some depend on the leather types.
		const leatherMaterial = leatherDescription.leatherMaterial as SeatMaterial;
		if (!leatherMaterial) {
			console.error('No leather material assigned for id: ', id);
			return;
		}
		const seatLeatherMaterial = leatherDescription.seatLeatherMaterial as SeatMaterial;
		if (!seatLeatherMaterial) {
			console.error('No seat leather material assigned for id: ', id);
			return;
		}
		const doorLeatherMaterial = leatherDescription.doorLeatherMaterial as SeatMaterial;
		if (!doorLeatherMaterial) {
			console.error('No door leather material assigned for id: ', id);
			return;
		}

		const textileMaterial = leatherDescription.textileMaterial as SeatTextileMaterial;
		const seatTextileOrLeatherMaterial = isTwoTone ? leatherDescription.seatTextileMaterial as SeatMaterial : seatLeatherMaterial;
		const doorTextileOrLeatherMaterial = isTwoTone ? leatherDescription.textileMaterial as SeatMaterial : doorLeatherMaterial;

		// if the leather type or contrast option has changed (or on first call), set the relevant meshes to the relevant materials.
		if (leatherType !== this._lastLeatherType || this._lastContrastOptionId !== contrastOptionId) {

			// leather material.
			this._gearstickLeather.material = leatherMaterial;
			this._gearstickLeather400.material = leatherMaterial;
			this._handbrakeLeather.material = leatherMaterial;
			this._visors.material = leatherMaterial;
			this._hardTopLeather.material = leatherMaterial;
			this._carpetPiping.material = leatherMaterial;

			// door leather material.
			this._doorLeather.material = doorLeatherMaterial;
			this._interiorSideScreen.material = doorLeatherMaterial;
			this._doorSills.material = doorLeatherMaterial;
			this._interiorRear.material = doorLeatherMaterial;
			this._tunnelLeather.material = doorLeatherMaterial;
			this._tunnelPocket.material = doorLeatherMaterial;
			this._dashboardLeather.material = doorLeatherMaterial;
			this._dashboardLeather400.material = doorLeatherMaterial;
			this._seatBackLeather.material = doorLeatherMaterial;

			// seat leather material.
			this._setOrFadeSeatMaterial(this._seatBase, seatLeatherMaterial, true);

			// either the textile or leather materials.
			this._setOrFadeSeatMaterial(this._seatBoxStitch, seatTextileOrLeatherMaterial, true);
			this._setOrFadeSeatMaterial(this._seatDiamondStitch, seatTextileOrLeatherMaterial, true);
			this._setOrFadeSeatMaterial(this._seatHorizontalStitch, seatTextileOrLeatherMaterial, true);
			
			// if Supersport 400...
			if (Configuration.isSupersport400Model()) {

				// the tunnel leather 400 mesh has a different material in order to support the extended stitching pattern,
				// so instead of setting it to the chosen material, we just need to fade the colour.
				this._setOrFadeSeatMaterial(this._tunnelLeather400, doorLeatherMaterial, false);
				
				// the door leather 400 mesh may also have a different material for the extended stitching pattern, so re-select the pleating style to set
				//the correct material while retaining the selected colour (from the seat base material).
				this.selectSeatPleatingStyle(Configuration.seatPleatingOption);

				// this will be set below to either leather or Alcántara.
				let seatCentreS400Material: SeatMaterial;

				// for two-tone or contrast options on Supersport 400, we need to apply a different material to the dashboard and door tops,
				// and use the Alcántara material for the seat centres for the S400 seat.
				if (isTwoTone || contrastOptionId !== 'NONE') {

					// disable the standard dashboard, and enable the 400 version, which has an Alcántara and a leather part.
					this.dashboardLeather.active = false;
					this.dashboardLeather400.active = true;
					this.dashboardAlcantara400.active = true;
					
					// for the contrast option...
					if (contrastOptionId !== 'NONE') {

						// set the dashboard and door tops with black Alcántara.
						this._dashboardAlcantara400.material = this.blackAlcantaraS400Material;
						this._doorTops400.material = this.blackAlcantaraS400Material;
					}

					// for two-tone options, set the dashboard and door tops with the textile material.
					else {
						this._dashboardAlcantara400.material = textileMaterial;
						this._doorTops400.material = textileMaterial;
					}

					// use the Alcántara material for the seat centres on the S400 seat.
					seatCentreS400Material = this.seatsS400AlcantaraMaterial;
				}

				// otherwise (for pebble or soft grain on Supersport 400)...
				else {

					// enable the standard dashboard, and disable the 400 version.
					this.dashboardLeather.active = true;
					this.dashboardLeather400.active = false;
					this.dashboardAlcantara400.active = false;

					// set the door tops to the leather material.
					this._doorTops400.material = doorLeatherMaterial;
					
					// use the leather material for the seat centres on the S400 seat.
					seatCentreS400Material = this.seatsS400LeatherMaterial;
				}
				
				// we also need to apply specific textures for the Supersport 400 seat centres, as the mesh has its own maps (as the shared maps used by the other seats are full),
				// so copy the textures and values below from the relevant seat material for Supersport 400 (leather or Alcántara) to a copy of the selected material.
				const material = seatTextileOrLeatherMaterial.clone() as SeatMaterial;
				material.albedoTexture = seatCentreS400Material.albedoTexture;
				material.metallicTexture = seatCentreS400Material.metallicTexture;
				material.normalTexture = seatCentreS400Material.normalTexture;
				material.detailNormalTexture = seatCentreS400Material.detailNormalTexture;
				material.stitchesMaskTexture = seatCentreS400Material.stitchesMaskTexture;
				material.metallicIntensity = seatCentreS400Material.metallicIntensity;
				material.smoothnessIntensity = seatCentreS400Material.smoothnessIntensity;
				material.detailNormalIntensity = seatCentreS400Material.detailNormalIntensity;

				// for the contrast option, set the seat colour to black, overriding the colour defined by the selected seat material.
				if (contrastOptionId !== 'NONE') {
					material.seatColor = this.blackAlcantaraS400Material.albedoColor;
					material.pipeColor = this.blackAlcantaraS400Material.albedoColor;
					material.stitchingColor = this.blackAlcantaraS400Material.albedoColor;

					// for the standard (horizontal stitch) seat centres, we can just set these to the black Alcántara material for standard seats.
					this._setOrFadeSeatMaterial(this._seatHorizontalStitch, this.seatsAlcantaraMaterial, true);
				}

				// set the material.
				this._setOrFadeSeatMaterial(this._seatVerticalStitch400, material, true);
			}

			// or for the standard model...
			else {
				
				// if two-tone, we need to set the textile material for the seat backs.
				if (isTwoTone) {
					this._seatBackTextile.material = doorTextileOrLeatherMaterial;
				}
			}
		}

		// otherwise, smoothly fade between the current material and the selected material.
		else {

			// leather material.
			this._fadeColour(this._visors, leatherMaterial);
			this._fadeColour(this._gearstickLeather, leatherMaterial);
			this._fadeColour(this._gearstickLeather400, leatherMaterial);
			this._fadeColour(this._handbrakeLeather, leatherMaterial);
			this._fadeColour(this._hardTopLeather, leatherMaterial);
			this._fadeColour(this._carpetPiping, leatherMaterial);

			// seat leather material.
			this._setOrFadeSeatMaterial(this._seatBase, seatLeatherMaterial, false);
			
			// door leather material.
			this._fadeColour(this._doorLeather, doorLeatherMaterial);
			this._fadeColour(this._dashboardLeather, doorLeatherMaterial);
			this._fadeColour(this._dashboardLeather400, doorLeatherMaterial);
			this._fadeColour(this._interiorSideScreen, doorLeatherMaterial);
			this._fadeColour(this._doorSills, doorLeatherMaterial);
			this._fadeColour(this._interiorRear, doorLeatherMaterial);
			this._fadeColour(this._tunnelLeather, doorLeatherMaterial);
			this._fadeColour(this._tunnelPocket, doorLeatherMaterial);
			this._fadeColour(this._seatBackLeather, doorLeatherMaterial);

			// either the textile or leather materials.
			// for seats, we only want to fade the seat colour, leaving the stitching colour unchanged, but only if the contrast option is not selected.
			if (!(Configuration.isSupersport400Model() && contrastOptionId !== 'NONE')) {
				this._setOrFadeSeatMaterial(this._seatBoxStitch, seatTextileOrLeatherMaterial, false);
				this._setOrFadeSeatMaterial(this._seatDiamondStitch, seatTextileOrLeatherMaterial, false);
				this._setOrFadeSeatMaterial(this._seatHorizontalStitch, seatTextileOrLeatherMaterial, false);
				this._setOrFadeSeatMaterial(this._seatVerticalStitch400, seatTextileOrLeatherMaterial, false);
			}
			
			if (Configuration.isSupersport400Model()) {

				// fade the tunnel leather 400 mesh retaining the stitching colour.
				this._setOrFadeSeatMaterial(this._tunnelLeather400, doorLeatherMaterial, false);

				// for the extended pleating style, we need to fade the seat, pipe and stitching colour.
				if (Configuration.seatPleatingOption === SeatPleatingStyles.ExtendedS400) {
					this._setOrFadeSeatMaterial(this._doorLeather400, doorLeatherMaterial, false);
				}

				// otherwise, for the standard pleating styles, we need to use a standard fade. 
				else {
					this._fadeColour(this._doorLeather400, doorLeatherMaterial);
				}
				
				// for two-tone options on the Supersport 400, we need to fade the albedo colour for the dashboard and door tops, as they are set to
				// standard textures not seat textures (which fade the seat, pipe and stitching colour instead).
				if (isTwoTone) {
					this._genericFadeColour(this._dashboardAlcantara400, textileMaterial.albedoColor, 'albedoColor' as FadableKey);
					this._genericFadeColour(this._doorTops400, textileMaterial.albedoColor, 'albedoColor' as FadableKey);
				}

				// otherwise (if not contrast option), fade the door tops as normal.
				else if (contrastOptionId === 'NONE') {
					this._fadeColour(this._doorTops400, doorLeatherMaterial);
				}
				// n.b. for the contrast option the Alcántara items remain black, so no need to do anything here.
			}
			else {

				// for standard two-tone, we need to fade the textile material for the seat backs.
				if (isTwoTone && textileMaterial) {
					this._genericFadeColour(this._seatBackTextile, textileMaterial.albedoColor, 'albedoColor' as FadableKey);
				}
			}
		}

		// update the 'last' variables.
		this._lastLeatherType = leatherType;
		this._lastContrastOptionId = contrastOptionId;
	}

	/**
	 * Gets the current stitching colour for the seat.
	 */
	getCurrentStitchingColour(): NumberArray {
		const seatMaterial = this._seatBoxStitch.material as SeatMaterial;
		return seatMaterial.stitchingColor;
	}
	
	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Provides special handling when setting or fading the seat materials, as we only want to set the seat colour, not the stitching colour,
	* unless it is set to match the leather colour.
	*/
	private _setOrFadeSeatMaterial(mesh: MeshComponent, seatLeatherMaterial: SeatMaterial, shouldSet: boolean) {

		// get the stitching colour from material currently assigned to the given mesh.
		const currentMaterial = mesh.material as SeatMaterial;
		const stitchingColour = currentMaterial.stitchingColor;

		// clone the given material.
		const newMaterial = seatLeatherMaterial.clone() as SeatMaterial;

		// set the stitching colour, unless it should match the leather colour.
		if (Configuration.seatStitchingOption !== Configuration.stitchingMatchingLeatherStyleId) {
			newMaterial.stitchingColor = stitchingColour;
		}

		// set or fade to the new material.
		if (shouldSet) {
			mesh.material = newMaterial;
		}
		else {
			this._fadeColour(mesh, newMaterial, false);
		}
	}

	/**
	* Smoothly fades the colour of any material on the given mesh between the current colour and the given colour, using a tween animation.
	*/
	private _genericFadeColour(
		mesh: MeshComponent,
		targetColour: NumberArray,
		targetProperty: FadableKey
	) {
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		const material = mesh.material.clone() as FadableMaterial;
		mesh.material = material;

		const prop = targetProperty as string;

		const colourTween = new Tween({
			[prop]: material[targetProperty],
		});
		colourTween
			.to(
				{
					[prop]: targetColour
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				(material as Record<FadableKey, NumberArray>)[targetProperty] = c[prop];
			})
			.easing(Easing.Quadratic.InOut);

		this._tweenManager.start(colourTween);
	}

	/**
	* Smoothly fades the colour of the material on the given mesh between the current colour and the colour defined by the given material,
	* using a tween animation to change the relevant material properties.
	*
	* Note that on the leather materials, the stitching colour property actually defines the main colour. We set all properties here for consistency.
	* The exception is the seat leather and seat textile materials, which only need the seat colour to be set, and support separate piping and stitching colours.
	*/
	private _fadeColour(mesh: MeshComponent, targetMaterial: SeatMaterial, onlySeatColour?: boolean) {

		// ensure the given mesh has a material assigned to it.
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		// clone the current material (as we don't want to change the source material itself).
		const material = mesh.material.clone() as SeatMaterial;
		mesh.material = material;

		// tween the relevant material properties to change the colour.
		const colourTween = new Tween({
			seatColor: material.seatColor,
			pipeColor: material.pipeColor,
			stitchingColor: material.stitchingColor
		});
		colourTween
			.to(
				{
					seatColor: targetMaterial.seatColor,
					pipeColor: targetMaterial.pipeColor,
					stitchingColor: targetMaterial.stitchingColor
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				material.seatColor = c.seatColor;

				// optionally tween all properties.
				if (!onlySeatColour) {
					material.pipeColor = c.pipeColor;
					material.stitchingColor = c.stitchingColor;
				}
			})
			.easing(Easing.Quadratic.InOut);
		this._tweenManager.start(colourTween);
	}
}

